import {
	FC,
	PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { Geolocation } from '@capacitor/geolocation';
import { debounce } from 'throttle-debounce';
import { useShallow } from 'zustand/react/shallow';

import { ILocationDecoded } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';
import { calculateDistance, Defer, LatLng } from '@mopla/utils';

import { reverseGeocode } from '../../hooks/location';

import {
	CACHED_POS_MAX_AGE,
	GEOCODING_DEBOUNCE_MS,
	GET_POS_TIMEOUT,
	POSITION_UPDATE_THRESHOLD_METERS,
} from './constants';
import {
	GeoPositionContext,
	IFetchPositionOptions,
	IFetchPositionResult,
} from './GeoPositionContext';
import { useGeolocationPermission } from './useGeolocationPermission';
import { useGeoPositionStore } from './useGeoPositionStore';

/**
 * Provider for geolocation with automatic permission handling and geocoding
 *
 * Features:
 * - Fetches position once on mount, if permissions granted
 * - Manual control of watchPosition (start/stop)
 * - Automatic geocoding with debounce (1s)
 * - Position updates only if distance > POSITION_UPDATE_THRESHOLD_METERS
 * - No custom UI, only native OS permission dialogs
 *
 * @example
 * // In components
 * const { fetchCurrentPosition, coordinates, address } = useGeoPosition();
 */
export const CurrentGeoPositionProviderV2: FC<PropsWithChildren> = ({
	children,
}) => {
	const logger = useLogger('CurrentGeoPositionV2');
	const { checkPermission } = useGeolocationPermission();

	/** Deduplicate concurrent calls: all callers share the same pending promise */
	const pendingFetchRef = useRef<Defer<IFetchPositionResult | null> | null>(
		null
	);

	const {
		setCoordinates,
		setAddress,
		setIsLoadingPosition,
		setIsGeocoding,
		setWatchingPositionId,
		setError,
		permissionStatus,
	} = useGeoPositionStore(
		useShallow((state) => ({
			setCoordinates: state.setCoordinates,
			setAddress: state.setAddress,
			setIsLoadingPosition: state.setIsLoadingPosition,
			setIsGeocoding: state.setIsGeocoding,
			setWatchingPositionId: state.setWatchingPositionId,
			setError: state.setError,
			permissionStatus: state.permissionStatus,
		}))
	);

	const geocodePosition = useCallback(
		async (coords: LatLng): Promise<ILocationDecoded | null> => {
			setIsGeocoding(true);
			setError(null);

			try {
				const decoded = await reverseGeocode(coords.toString(), 1);
				const address = decoded[0] || null;

				setAddress(address);

				return address;
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : 'Geocoding failed';
				setError(errorMessage);
				logger.warn('Geocoding failed', { error: errorMessage });

				return null;
			} finally {
				setIsGeocoding(false);
			}
		},
		[setAddress, setError, setIsGeocoding, logger]
	);

	/** Debounced handler for position changes */
	const handlePositionChange = useMemo(
		() =>
			debounce(GEOCODING_DEBOUNCE_MS, async (coords: LatLng) => {
				setCoordinates(coords);
				await geocodePosition(coords);
			}),
		[geocodePosition, setCoordinates]
	);

	/** Check if position should be updated based on threshold and shallow compare */
	const shouldUpdatePosition = useCallback(
		(oldCoords: LatLng | null, newCoords: LatLng): boolean => {
			if (!oldCoords) return true;

			/** Shallow compare coordinates (like distinctUntilChanged in RxJS) */
			if (
				oldCoords.coord.lat === newCoords.coord.lat &&
				oldCoords.coord.lng === newCoords.coord.lng
			) {
				return false;
			}

			/** Update only if distance > POSITION_UPDATE_THRESHOLD_METERS */
			const distance = calculateDistance(oldCoords, newCoords);

			return distance > POSITION_UPDATE_THRESHOLD_METERS;
		},
		[]
	);

	const fetchCurrentPosition = useCallback(
		async (
			options?: IFetchPositionOptions
		): Promise<IFetchPositionResult | null> => {
			if (pendingFetchRef.current) {
				return pendingFetchRef.current.promise;
			}

			const currentPermissionStatus =
				useGeoPositionStore.getState().permissionStatus;

			if (currentPermissionStatus?.location !== 'granted') return null;

			const deferred = new Defer<IFetchPositionResult | null>();
			pendingFetchRef.current = deferred;

			setIsLoadingPosition(true);
			setError(null);

			try {
				const position = await Geolocation.getCurrentPosition({
					enableHighAccuracy: true,
					timeout: GET_POS_TIMEOUT,
					maximumAge: options?.maximumAge ?? 0,
				});

				const coordinates = new LatLng(
					{
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					},
					position.coords.accuracy
				);

				setCoordinates(coordinates);
				const address = await geocodePosition(coordinates);

				const result: IFetchPositionResult = {
					coordinates,
					address,
				};

				deferred.resolve(result);

				return result;
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : 'Failed to get position';

				setError(errorMessage);
				logger.warn('Failed to get position', { error: errorMessage });
				deferred.resolve(null);
				return null;
			} finally {
				setIsLoadingPosition(false);
				pendingFetchRef.current = null;
			}
		},
		[geocodePosition, setCoordinates, setError, setIsLoadingPosition, logger]
	);

	/**
	 * Start watching position.
	 * Note that watching for location changes can consume a large amount of energy.
	 * Be smart about listening only when you need to.
	 * */
	const startWatchingPosition = useCallback(async () => {
		const { permissionStatus: currentPermissionStatus, watchingPositionId } =
			useGeoPositionStore.getState();

		if (currentPermissionStatus?.location !== 'granted') {
			logger.info(
				'Trying to start watching position, but the location permission is not granted',
				{
					currentPermissionStatus,
				}
			);
			return;
		}

		if (watchingPositionId) return;

		try {
			const watchId = await Geolocation.watchPosition(
				{
					enableHighAccuracy: true,
					timeout: GET_POS_TIMEOUT,
					maximumAge: CACHED_POS_MAX_AGE,
				},
				(position, err) => {
					if (err) {
						logger.warn('Watch position error', { error: String(err) });
						return;
					}

					if (position) {
						const newCoords = new LatLng(
							{
								lat: position.coords.latitude,
								lng: position.coords.longitude,
							},
							position.coords.accuracy
						);

						const { coordinates: currentCoords } =
							useGeoPositionStore.getState();
						/** Only update if threshold exceeded */
						if (shouldUpdatePosition(currentCoords, newCoords)) {
							handlePositionChange(newCoords);
						}
					}
				}
			);

			setWatchingPositionId(watchId);
		} catch (err) {
			logger.warn('Failed to start watching position', { error: String(err) });
			setWatchingPositionId(null);
		}
	}, [
		shouldUpdatePosition,
		handlePositionChange,
		setWatchingPositionId,
		logger,
	]);

	/** Stop watching position */
	const stopWatchingPosition = useCallback(() => {
		const { watchingPositionId } = useGeoPositionStore.getState();

		if (watchingPositionId) {
			Geolocation.clearWatch({ id: watchingPositionId });
		}
		handlePositionChange.cancel();
		setWatchingPositionId(null);
	}, [setWatchingPositionId, handlePositionChange]);

	/** Context value with methods */
	const contextValue = useMemo(
		() => ({
			fetchCurrentPosition,
			startWatchingPosition,
			stopWatchingPosition,
		}),
		[fetchCurrentPosition, startWatchingPosition, stopWatchingPosition]
	);

	/** Check permissions on mount */
	useEffect(() => {
		checkPermission();
	}, [checkPermission]);

	/** Fetch position when permission is granted and no coordinates yet */
	useEffect(() => {
		if (
			permissionStatus?.location === 'granted' &&
			!useGeoPositionStore.getState().coordinates
		) {
			fetchCurrentPosition({ maximumAge: CACHED_POS_MAX_AGE });
		}
	}, [permissionStatus, fetchCurrentPosition]);

	/** Cleanup on unmount */
	useEffect(() => {
		return () => {
			stopWatchingPosition();
		};
	}, [stopWatchingPosition]);

	return (
		<GeoPositionContext.Provider value={contextValue}>
			{children}
		</GeoPositionContext.Provider>
	);
};
